import { queryClient } from '../context/Api.context';
import { Cornerstone } from '@rpi/api';
import React from 'react';
import { z } from 'zod';

const SettingsSchema = z.object({
  email: z.string().email().optional().nullable(),
  firstName: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  preferredCurrency: z
    .nativeEnum(Cornerstone.SupportedCurrency)
    .optional()
    .nullable(),
  languageCode: z.string().optional().nullable(),
  wantsNewsletter: z.boolean().optional().nullable(),
  // note this is a placeholder for locale until it is implemented
  locale: z.string().optional().nullable(),
});

export type SettingsType = z.infer<typeof SettingsSchema>;

export const useSettingsHook = () => {
  const [settings, setSettings] = React.useState<SettingsType | null>(null);
  const { data } = Cornerstone.useMe({
    query: {
      queryKey: ['me'],
    },
  });

  React.useEffect(() => {
    if (data?.data) {
      setSettings(SettingsSchema.parse(data.data));
    }
  }, [data]);

  const currencyFormatter = React.useCallback(
    (value?: Cornerstone.MonetaryAmount) => {
      if (!settings || value === undefined) {
        return '';
      }
      const formatter = new Intl.NumberFormat(settings.locale ?? 'en-US', {
        style: 'currency',
        currency: value.currency,
      });

      return formatter.format(value.amount);
    },
    [settings],
  );

  const handleInvalidate = () => {
    queryClient.invalidateQueries(['me']);
  };

  return {
    settings,
    handleInvalidate,
    currencyFormatter,
  };
};
